import { useState, useRef, PropsWithChildren, useCallback } from "react"
import { ListItem } from "@rmwc/list"
import "@rmwc/list/styles"
import { useOnClickOutside } from "../utils/useOnClickOutside"
import {
  Container,
  List,
  Relative,
  StyledChevronDown,
  StyledChevronUp,
  FilterItem,
  Separator,
  StyledFilter,
  StyledLabel,
} from "./DropdownButtonStyles"
import { Button } from "./Button"
import { CSSFn } from "../styles"
import { Direction, Variant } from "./types"
import { Chevron } from "../Icons"

export type DropdownButtonItem<T> = {
  text: string
  value: T
  dataTestId?: string
}

export type DropdownButtonProps<T> = {
  defaultDisplayValue?: string
  value?: T
  action: (value: T) => void
  firstItem?: DropdownButtonItem<T>
  lastItem?: DropdownButtonItem<T>
  items: DropdownButtonItem<T>[]
  css?: {
    root?: CSSFn
    label?: CSSFn
  }
  variant: Variant
  direction?: Direction
  disabled?: boolean
  dataTestIds?: {
    button?: string
    list?: string
  }
  matchByDisplay?: boolean
  label?: string
}
export const DropdownButton = <T extends unknown>({
  defaultDisplayValue,
  value,
  action,
  css,
  direction = `down`,
  disabled,
  firstItem,
  items,
  lastItem,
  dataTestIds,
  variant,
  matchByDisplay,
  label,
}: DropdownButtonProps<T>) => {
  const [isOpen, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useOnClickOutside(ref, () => setOpen(false))
  const DownChevron = (
    <StyledChevronDown>
      <Chevron dark={variant === `pagination`} />
    </StyledChevronDown>
  )
  const UpChevron = (
    <StyledChevronUp>
      <Chevron dark={variant === `pagination`} />
    </StyledChevronUp>
  )
  const IconOpen = direction === `down` ? DownChevron : UpChevron
  const IconClose = direction === `down` ? UpChevron : DownChevron
  const Icon = !isOpen ? IconOpen : IconClose
  const ItemWrap = useCallback(
    // eslint-disable-next-line react/no-unused-prop-types
    ({ children, selected, islast }: PropsWithChildren<{ selected: boolean; islast: boolean }>) =>
      variant === `pagination` ? (
        <>{children}</>
      ) : (
        <FilterItem selected={selected} isLast={islast}>
          {children}
        </FilterItem>
      ),
    [variant]
  )
  const Item = useCallback(
    // eslint-disable-next-line react/no-unused-prop-types, react/require-default-props
    ({ item, isLast }: { item: DropdownButtonItem<T>; isLast?: boolean }) => (
      <ListItem
        data-testid={item.dataTestId}
        onClick={() => {
          action(item.value)
          setOpen(false)
        }}
      >
        <ItemWrap
          selected={
            !matchByDisplay
              ? value
                ? value === item.value
                : defaultDisplayValue === item.text
              : defaultDisplayValue === item.text
          }
          islast={isLast ?? false}
        >
          {item.text}
        </ItemWrap>
      </ListItem>
    ),
    [ItemWrap, action, defaultDisplayValue, value, matchByDisplay]
  )
  return (
    <Relative ref={ref} css={css?.root}>
      <Container>
        <StyledFilter>
          {label && <StyledLabel>{label}</StyledLabel>}
          <Button
            variant={variant}
            disabled={disabled}
            onClick={() => setOpen(!isOpen)}
            data-testid={dataTestIds?.button}
          >
            {[firstItem, ...items, lastItem].find((it) => it?.value === value)?.text || defaultDisplayValue}
            {Icon}
          </Button>
        </StyledFilter>
      </Container>
      {isOpen && (
        <List direction={direction} variant={variant} data-testid={dataTestIds?.list}>
          {firstItem && (
            <>
              <Item item={firstItem} />
              <Separator />
            </>
          )}
          {items.map((item) => (
            <Item key={item.text} item={item} />
          ))}
          {lastItem && (
            <>
              <Separator />
              <Item item={lastItem} isLast />
            </>
          )}
        </List>
      )}
    </Relative>
  )
}

DropdownButton.defaultProps = {
  value: undefined,
  firstItem: undefined,
  lastItem: undefined,
  defaultDisplayValue: undefined,
  disabled: false,
  dataTestIds: undefined,
  direction: `down`,
  css: undefined,
  matchByDisplay: undefined,
  label: null,
}
